import { ChildrenProp, ClassNameProp } from '../common-types';
import React from 'react';

/**
 * Props for a {@link D3Tooltip} component.
 * @author agd
 */
export interface D3TooltipProps extends Required<ChildrenProp>, ClassNameProp {
  /**
   * Position of the tooltip.
   */
  position: {
    /**
     * X position of the tooltip.
     */
    x: number;
    /**
     * y position of the tooltip.
     */
    y: number;
  };
  /**
   * If the tooltip is visible or not.
   */
  visible: boolean;
  /**
   * Tooltip width.
   */
  width: number;
  /**
   * Tooltip height.
   */
  height: number;
  /**
   * The x-position constraint of the D3Tooltip.
   * This is used to determine if the tooltip should be constrained inside this value.
   */
  constrainX?: number;
}

/**
 * D3Tooltip component to create an SVG tooltip.
 * The actual content of the tooltip itself is determined by the passed children.
 *
 * @param props a {@link D3TooltipProps} object
 * @return the created {@link JSX.Element}
 * @constructor
 * @author agd
 */
export function D3Tooltip({
  visible = false,
  position,
  width,
  height,
  children,
  constrainX,
  className = ''
}: D3TooltipProps): JSX.Element {
  let visibility = 'hidden';
  // determine position
  const x = position.x - width / 2;
  const y = position.y;

  // transform definitions
  let transform = '';
  const transformText = 'translate(10, 20)';
  let transformArrow = '';

  // adapt transform if visible
  if (visible) {
    visibility = 'visible';
    // determine x-axis constraint
    let xConstrain = 0;
    if (constrainX) {
      if (x < 0) {
        xConstrain = x;
      } else if (x + width > constrainX) {
        xConstrain = -(constrainX - width - x);
      }
    }
    const tooltipX = x - xConstrain;
    // ensure the arrow is not positioned to be outside the tooltip text rectangle
    // which would otherwise be possible due to the arrow having a starting position of 5,0 instead of 0,0
    // 15 here is anchor arrow width / 2 + 5
    // width / 2 - 15 + xConstrain = regular positioning including constraint, with a minimum result of 0 (0 essentially equates 5,0)
    // width - 30 = maximum right position (30 is arrow width of 20 + left and right 5
    const arrowX = Math.min(width / 2 - 15 + xConstrain, width - 30);

    if (y > height) {
      transform = `translate(${tooltipX}, ${y - height - 20})`;
      transformArrow = `translate(${arrowX}, ${height - 2})`;
    } else if (y < height) {
      transform = `translate(${tooltipX}, ${y + 20})`;
      transformArrow = `translate(${arrowX},0) rotate(180,15,1)`;
    }
  }

  return (
    <g
      transform={transform}
      className={`ehs-d3-tooltip ${className}`.trim()}
      width={width}
      height={height}
    >
      <rect
        width={width}
        height={height}
        rx="5"
        ry="5"
        visibility={visibility}
      />
      <polygon
        points="5,0  25,0  15,10"
        transform={transformArrow}
        visibility={visibility}
      />
      <text visibility={visibility} transform={transformText}>
        {children}
      </text>
    </g>
  );
}
